/**
 * Whether the reopened chat is still restoring its last screen, so a live interactive session should not start yet.
 *
 * The restore happens in stages, each of which used to be invisible to the session start:
 *   0. the agent list is fetched (the config query is skipped until an agent id is known);
 *   1. the chat config is fetched (it names the default session);
 *   2. the default session is pinned (`loadSession`), which fetches its history;
 *   3. the history yields a `hydrate` placeholder card, whose rows are fetched a moment later.
 * Starting the session before all three finish makes it begin on an empty screen (only the entry tools) and get
 * corrected seconds later, so the model is one step late on "what is open".
 */
export interface RestoreState {
  /** The agent id is not known yet (the agent list is still loading), so the chat config query has not started. */
  assistantMissing: boolean;
  /** The chat-config query is still in flight. */
  configLoading: boolean;
  /** A `loadSession` call is in flight (history being fetched). */
  sessionLoading: boolean;
  /** The thread holds a `hydrate` card placeholder whose rows have not been fetched yet. */
  hasHydratePlaceholder: boolean;
  /** The config pins the chat to a default session (multi-thread off). */
  pinsDefaultSession: boolean;
  /** The session the config names, if any. */
  defaultSessionId?: string | null;
  /** The session currently open. */
  conversationId: string;
}

export function isRestorePending(s: RestoreState): boolean {
  // Until the agent is known the config query is SKIPPED, which reads as "not loading": treat it as pending.
  if (s.assistantMissing) return true;
  if (s.configLoading) return true;
  if (s.sessionLoading) return true;
  if (s.hasHydratePlaceholder) return true;
  // Config is in, the chat is pinned to a default session, but that session has not been picked up yet.
  if (s.pinsDefaultSession && s.defaultSessionId && s.defaultSessionId !== s.conversationId) return true;
  return false;
}
